/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Sparkles, Mail, Building2, AlertCircle, Loader2, Check, Plus, RefreshCw, ChevronDown, ChevronUp, FlaskConical, Truck,
} from 'lucide-react';

const API = '/api';

export default function DedupePharmaContactsPage() {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [orphanSearch, setOrphanSearch] = useState('');
  const [confirmApply, setConfirmApply] = useState(null); // 'emails' | 'companies' | 'all'
  const [creatingProfile, setCreatingProfile] = useState({}); // company_name → bool
  const [stubNames, setStubNames] = useState('');
  const [stubBusy, setStubBusy] = useState(false);
  const [stubResult, setStubResult] = useState(null);
  const [stubOpen, setStubOpen] = useState(false);

  const handleBulkAddStubs = async () => {
    const names = stubNames.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
    if (names.length === 0) {
      toast.error('Paste at least one company name');
      return;
    }
    setStubBusy(true);
    setStubResult(null);
    try {
      const res = await axios.post(`${API}/admin/no-contact-pharma/bulk-add`,
        { names },
        { withCredentials: true });
      setStubResult(res.data);
      toast.success(`Created ${res.data?.created_count || 0} new pharma stubs · ${res.data?.already_existed_count || 0} already existed`);
    } catch (e) {
      toast.error('Bulk add failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setStubBusy(false);
    }
  };

  const runDryRun = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/dedupe-pharma-contacts`,
        { mode: 'dry_run', min_score: 95, normalize_emails: true },
        { withCredentials: true });
      setResult(data);
      toast.success('Dry-run complete — review below before committing.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run dry-run');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runDryRun(); }, []);

  const applyChanges = async (scope) => {
    // scope: 'emails' | 'companies' | 'all'
    setApplying(true);
    try {
      const body = {
        mode: 'apply',
        min_score: 95,
        normalize_emails: scope === 'emails' || scope === 'all',
      };
      const { data } = await axios.post(`${API}/admin/dedupe-pharma-contacts`,
        body, { withCredentials: true });
      const s = data.summary || {};
      toast.success(
        `Applied: ${s.email_applied || 0} emails normalised, ${s.company_applied || 0} companies canonicalised.`
      );
      setResult(data);
      setConfirmApply(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const createProfileFromOrphan = async (companyName, category = 'pharma') => {
    setCreatingProfile(prev => ({ ...prev, [companyName]: true }));
    try {
      if (category === 'biotech') {
        // Biotech goes into a different collection (biotech_companies)
        await axios.post(`${API}/biotech-companies`, {
          company_name: companyName,
          is_my_company: false,
          notes: 'Created from pharma-contacts cleanup — verify and enrich.',
        }, { withCredentials: true });
        toast.success(`Added "${companyName}" as a Biotech`);
      } else {
        // pharma or regional_distributor → company_profiles
        await axios.post(`${API}/company-profiles`, {
          company_name: companyName,
          category,
          company_type: category === 'regional_distributor' ? 'Distributor' : 'Other',
          therapeutic_interests: [],
          disease_areas: {},
          notes: 'Created from pharma-contacts cleanup — verify and enrich.',
        }, { withCredentials: true });
        const label = category === 'regional_distributor' ? 'Distributor' : 'Pharma';
        toast.success(`Added "${companyName}" as a ${label}`);
      }
      // Remove this orphan from the result locally so the row vanishes
      setResult(prev => prev ? {
        ...prev,
        no_match_top: (prev.no_match_top || []).filter(o => o.company_name !== companyName),
      } : prev);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to add ${companyName}`);
    } finally {
      setCreatingProfile(prev => ({ ...prev, [companyName]: false }));
    }
  };

  const s = result?.summary || {};
  const orphans = (result?.no_match_top || []).filter(o =>
    !orphanSearch.trim() ||
    o.company_name.toLowerCase().includes(orphanSearch.trim().toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="dedupe-pharma-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Sparkles className="w-6 h-6 text-violet-600" />
            Pharma Contacts Cleanup
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Normalise emails and canonicalise company names across all pharma contacts. Dry-run shows what would change; nothing is modified until you click Apply.
          </p>
        </div>
        <Button onClick={runDryRun} disabled={loading} variant="outline" data-testid="dedupe-rerun-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Re-scan
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard icon={<Building2 className="w-4 h-4 text-slate-500" />} label="Pharma contacts" value={s.total_contacts ?? '—'} />
        <SummaryCard icon={<Mail className="w-4 h-4 text-amber-600" />} label="Emails to fix" value={s.email_changes ?? '—'} accent={s.email_changes > 0 ? 'amber' : 'slate'} />
        <SummaryCard icon={<Sparkles className="w-4 h-4 text-violet-600" />} label="Auto-canonical" value={s.company_changes_auto ?? '—'} accent={s.company_changes_auto > 0 ? 'violet' : 'slate'} />
        <SummaryCard icon={<AlertCircle className="w-4 h-4 text-rose-600" />} label="Ambiguous" value={s.company_ambiguous ?? '—'} accent={s.company_ambiguous > 0 ? 'rose' : 'slate'} />
        <SummaryCard icon={<Building2 className="w-4 h-4 text-emerald-700" />} label="Orphans (no CRM match)" value={s.company_no_match ?? '—'} accent={s.company_no_match > 0 ? 'emerald' : 'slate'} />
      </div>

      <Card className="border-amber-200 bg-amber-50/40">
        <CardHeader className="pb-3 cursor-pointer" onClick={() => setStubOpen(o => !o)} data-testid="bulk-add-stubs-toggle">
          <CardTitle className="text-base flex items-center justify-between text-slate-800">
            <span className="inline-flex items-center gap-2">
              <Plus className="w-4 h-4 text-amber-700" />
              Bulk Add No-Contact Pharma <span className="text-xs font-normal text-slate-500">(annual-review register)</span>
            </span>
            {stubOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </CardTitle>
        </CardHeader>
        {stubOpen && (
          <CardContent className="pt-0 space-y-3">
            <p className="text-xs text-slate-600">
              Tail-end of the AI-mapping coverage loop: AI flagged a target → you tried to find data → couldn't → stub the company here so it appears on the <strong>Pharma</strong> list for annual review. Each stub is idempotent (existing names are skipped, never duplicated).
            </p>
            <textarea
              value={stubNames}
              onChange={(e) => setStubNames(e.target.value)}
              rows={6}
              placeholder={"Paste names — one per line:\nAerie Pharmaceuticals\nMeiji Seika Pharma\nHanwha Solutions\nMeda AB\nOcuphire Pharma"}
              className="w-full text-xs font-mono p-2 border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-amber-300"
              data-testid="bulk-add-stubs-textarea"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-slate-500">
                {stubNames.trim() ? `${stubNames.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean).length} name(s) ready` : 'No names yet'}
              </span>
              <Button onClick={handleBulkAddStubs} disabled={stubBusy || !stubNames.trim()} size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="bulk-add-stubs-btn">
                {stubBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                Create pharma stubs
              </Button>
            </div>
            {stubResult && (
              <div className="text-[11px] space-y-1.5" data-testid="bulk-add-stubs-result">
                {stubResult.created?.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <p className="font-semibold text-emerald-800">✅ Created ({stubResult.created_count}):</p>
                    <p className="text-emerald-700 mt-0.5">{stubResult.created.join(', ')}</p>
                  </div>
                )}
                {stubResult.already_existed?.length > 0 && (
                  <div className="bg-slate-100 border border-slate-200 rounded p-2">
                    <p className="font-semibold text-slate-700">ℹ️ Already in CRM ({stubResult.already_existed_count}) — skipped:</p>
                    <ul className="text-slate-600 mt-0.5 space-y-0.5">
                      {stubResult.already_existed.map((x, i) => (
                        <li key={i}>
                          <strong>{x.input}</strong> → <span className="italic">{x.existing_name}</span>
                          <Badge className="ml-1.5 text-[9px] bg-white text-slate-600 border border-slate-200">{x.category}</Badge>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <Tabs defaultValue="emails" className="w-full">
        <TabsList>
          <TabsTrigger value="emails" data-testid="tab-emails">📧 Emails ({s.email_changes ?? 0})</TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">🏢 Companies ({(s.company_changes_auto ?? 0) + (s.company_ambiguous ?? 0)})</TabsTrigger>
          <TabsTrigger value="orphans" data-testid="tab-orphans">🆔 Orphans ({s.company_no_match ?? 0})</TabsTrigger>
        </TabsList>

        {/* Tab 1: Email normalisation */}
        <TabsContent value="emails" className="space-y-3 mt-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-slate-500">
              Contacts with mixed-case or whitespace in email. Risk-free fix — lowercase + trim.
            </p>
            {(s.email_changes ?? 0) > 0 && (
              <Button
                onClick={() => setConfirmApply('emails')}
                disabled={applying}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="apply-emails-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Apply all {s.email_changes} email fixes
              </Button>
            )}
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Contact</TableHead>
                  <TableHead className="text-xs">Current email</TableHead>
                  <TableHead className="text-xs">Will become</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(result?.email_changes || []).length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-6 text-sm text-slate-500">No email fixes needed.</TableCell></TableRow>
                ) : (
                  (result?.email_changes || []).map((c, i) => (
                    <TableRow key={i} data-testid={`email-change-${i}`}>
                      <TableCell className="text-sm">{c.name}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-500 line-through">{c.old}</TableCell>
                      <TableCell className="text-xs font-mono text-emerald-700">{c.new}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab 2: Company canonicalisation */}
        <TabsContent value="companies" className="space-y-3 mt-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-slate-500">
              Fuzzy-matched at score ≥ 95 → auto-apply. Score 88–94 or multiple competing matches → ambiguous (manual review).
            </p>
            {(s.company_changes_auto ?? 0) > 0 && (
              <Button
                onClick={() => setConfirmApply('companies')}
                disabled={applying}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="apply-companies-btn"
              >
                {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Apply {s.company_changes_auto} auto-canonical fixes
              </Button>
            )}
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Contact</TableHead>
                  <TableHead className="text-xs">Current company</TableHead>
                  <TableHead className="text-xs">Canonical match</TableHead>
                  <TableHead className="text-xs">Score</TableHead>
                  <TableHead className="text-xs">Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(result?.company_changes || []).length === 0 && (result?.ambiguous || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-sm text-slate-500">No canonicalisation candidates — your data is clean here.</TableCell></TableRow>
                ) : (
                  <>
                    {(result?.company_changes || []).map((c, i) => (
                      <TableRow key={`auto-${i}`} className="bg-violet-50/30" data-testid={`auto-canon-${i}`}>
                        <TableCell className="text-sm">{c.contact_name}</TableCell>
                        <TableCell className="text-xs text-slate-500 line-through">{c.old_company}</TableCell>
                        <TableCell className="text-xs font-semibold text-violet-700">{c.new_company}</TableCell>
                        <TableCell><Badge className="bg-violet-100 text-violet-800 border border-violet-200 text-[10px]">{c.score}</Badge></TableCell>
                        <TableCell className="text-[10px] text-slate-500">{c.match_type}</TableCell>
                      </TableRow>
                    ))}
                    {(result?.ambiguous || []).map((c, i) => (
                      <TableRow key={`amb-${i}`} className="bg-rose-50/30" data-testid={`amb-canon-${i}`}>
                        <TableCell className="text-sm">{c.contact_name}</TableCell>
                        <TableCell className="text-xs text-slate-500">{c.old_company}</TableCell>
                        <TableCell className="text-xs text-rose-700">{c.new_company} <span className="text-[10px] text-rose-500">(needs review)</span></TableCell>
                        <TableCell><Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[10px]">{c.score}</Badge></TableCell>
                        <TableCell className="text-[10px] text-slate-500">{c.match_type}</TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Tab 3: Orphans — companies in contacts but not in CRM */}
        <TabsContent value="orphans" className="space-y-3 mt-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-slate-500">
              Companies that have contacts in your DB but no parent profile. Pick the right bucket per row — <strong>Pharma</strong> (drug-makers / BD targets), <strong>Biotech</strong> (innovator drug-developers), or <strong>Distributor</strong> (regional in-licensers like Tamer, Lunatus, Tabuk). Each creates a properly-shaped record so the company surfaces in AI mapping + filters.
            </p>
            <Input
              value={orphanSearch}
              onChange={e => setOrphanSearch(e.target.value)}
              placeholder="Filter orphans…"
              className="max-w-xs"
              data-testid="orphan-search"
            />
          </div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Company name (orphan)</TableHead>
                  <TableHead className="text-xs">Contacts</TableHead>
                  <TableHead className="text-xs text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphans.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center py-6 text-sm text-slate-500">No orphans matching your filter.</TableCell></TableRow>
                ) : (
                  orphans.map((o, i) => (
                    <TableRow key={i} data-testid={`orphan-${i}`}>
                      <TableCell className="text-sm font-medium">{o.company_name}</TableCell>
                      <TableCell><Badge className="bg-slate-100 text-slate-700 text-[10px]">{o.contact_count}</Badge></TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-[11px] h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              disabled={!!creatingProfile[o.company_name]}
                              data-testid={`orphan-add-${i}`}
                            >
                              {creatingProfile[o.company_name] ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                              Add as ▾
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="text-xs">
                            <DropdownMenuItem
                              onClick={() => createProfileFromOrphan(o.company_name, 'pharma')}
                              data-testid={`orphan-add-pharma-${i}`}
                              className="text-xs"
                            >
                              <Building2 className="w-3 h-3 mr-2 text-sky-600" />
                              Pharma
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => createProfileFromOrphan(o.company_name, 'biotech')}
                              data-testid={`orphan-add-biotech-${i}`}
                              className="text-xs"
                            >
                              <FlaskConical className="w-3 h-3 mr-2 text-emerald-600" />
                              Biotech
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => createProfileFromOrphan(o.company_name, 'regional_distributor')}
                              data-testid={`orphan-add-distributor-${i}`}
                              className="text-xs"
                            >
                              <Truck className="w-3 h-3 mr-2 text-amber-600" />
                              Distributor
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Apply confirmation dialog */}
      <AlertDialog open={!!confirmApply} onOpenChange={(o) => !o && setConfirmApply(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmApply === 'emails' && `Apply ${s.email_changes ?? 0} email normalisations?`}
              {confirmApply === 'companies' && `Apply ${s.company_changes_auto ?? 0} company canonicalisations?`}
              {confirmApply === 'all' && `Apply ALL changes (${s.email_changes ?? 0} emails + ${s.company_changes_auto ?? 0} companies)?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Changes are written to the database immediately. A backup log is kept in `pharma_contact_dedupe_log` for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={applying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-violet-600 hover:bg-violet-700"
              disabled={applying}
              onClick={() => applyChanges(confirmApply)}
              data-testid="confirm-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryCard({ icon, label, value, accent = 'slate' }) {
  const accentClasses = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50/40',
    violet: 'border-violet-200 bg-violet-50/40',
    rose: 'border-rose-200 bg-rose-50/40',
    emerald: 'border-emerald-200 bg-emerald-50/40',
  };
  return (
    <Card className={`${accentClasses[accent]} shadow-none`}>
      <CardHeader className="px-3 pt-3 pb-1">
        <CardTitle className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5 font-medium">
          {icon} {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-0">
        <div className="text-2xl font-semibold text-slate-900">{value}</div>
      </CardContent>
    </Card>
  );
}
